import React from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import {
  TrendingUp, Newspaper, Globe, Sparkles, Radio, Lock,
  Download, RotateCcw, ArrowUpRight, ArrowRight, Share2, Heart, LineChart,
} from "lucide-react";
import { Link } from "react-router-dom";

const Card = ({ icon: Icon, title, meta, span = "md:col-span-6", children, testid }) => (
  <div data-testid={testid} className={`${span} bg-[#070707] p-6 md:p-8`}>
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-[#FF5000]" />
        <h3 className="text-[12px] tracking-[0.28em] uppercase text-neutral-300">{title}</h3>
      </div>
      {meta && <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{meta}</span>}
    </div>
    {children}
  </div>
);

const ArticleRow = ({ a }) => (
  <a
    href={a.url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-start gap-3 py-3.5 group"
  >
    <ArrowUpRight className="w-3.5 h-3.5 text-neutral-600 group-hover:text-[#FF5000] mt-1 flex-shrink-0 transition-colors" />
    <div className="min-w-0">
      <p className="text-neutral-100 text-[14px] leading-snug group-hover:text-white">{a.title}</p>
      <p className="text-[11px] uppercase tracking-[0.16em] text-neutral-600 mt-1 ds-mono">
        {a.source}{a.country ? ` · ${a.country}` : ""}{a.date ? ` · ${a.date}` : ""}
      </p>
    </div>
  </a>
);

const Move = ({ pct, label }) => {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span className="ds-mono text-[12px] whitespace-nowrap">
      <span className={up ? "text-[#3ED598]" : "text-[#FF4D4D]"}>
        {up ? "up" : "down"} {Math.abs(pct)}%
      </span>
      <span className="text-neutral-600 ml-1.5">{label}</span>
    </span>
  );
};

const MarketRow = ({ s }) => (
  <li className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 py-3">
    <span className="text-[14px] truncate">
      <span className={s.is_index ? "text-neutral-300" : "text-white"}>{s.name}</span>
      <span className="text-neutral-600 ds-mono text-[11px] ml-2">{s.symbol}</span>
    </span>
    <span className="flex items-center gap-4">
      <Move pct={s.day_pct} label="today" />
      <Move pct={s.month_pct} label="this month" />
    </span>
  </li>
);

function exportXlsx(report) {
  const wb = XLSX.utils.book_new();
  const add = (name, rows) =>
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows || []), name.slice(0, 31));
  add("Summary", [{ location: report.location, industry: report.industry, summary: report.summary, generated_at: report.generated_at }]);
  add("Search Suggestions", (report.trends?.suggestions || []).map((q) => ({ query: q })));
  add("Trends Rising", (report.trends?.rising || []));
  add("Trends Top", (report.trends?.top || []));
  add("News", report.news);
  add("Global Coverage", report.coverage);
  add("Market Movement", (report.market?.signals || []).map((s) => ({
    name: s.name, ticker: s.symbol, price: s.price,
    change_today_pct: s.day_pct, change_30d_pct: s.month_pct,
  })));
  add("Opportunities", report.opportunities);
  const safe = `${report.industry}-${report.location}`.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  XLSX.writeFile(wb, `datasniper-${safe}.xlsx`);
}

export default function ReportView({ report, onReset }) {
  const t = report.trends || {};
  const news = report.news || [];
  const coverage = report.coverage || [];
  const opps = report.opportunities || [];
  const market = report.market && (report.market.signals || []).length ? report.market : null;

  const shareUrl = `${window.location.origin}/report?location=${encodeURIComponent(report.location)}&industry=${encodeURIComponent(report.industry)}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `DataSniper · ${report.industry} in ${report.location}`, url: shareUrl });
        return;
      } catch { /* user cancelled — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Shareable link copied to clipboard");
    } catch {
      toast.error("Couldn't copy the link — try again.");
    }
  };

  return (
    <div data-testid="report-view" className="ds-fade-up">
      {/* Live data banner */}
      <div
        data-testid="report-live-banner"
        className="border border-[#FF5000]/40 bg-[#FF5000]/[0.06] p-5 md:p-6 mb-px flex flex-col md:flex-row md:items-center gap-4"
      >
        <div className="flex items-start gap-3 flex-1">
          <Radio className="w-5 h-5 text-[#FF5000] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white text-[14px] font-semibold tracking-tight flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#FF5000] ds-pulse" /> Live data
            </p>
            <p className="text-neutral-400 text-[13px] leading-relaxed mt-1">
              Pulled live from <span className="text-neutral-200">Google Trends</span>,{" "}
              <span className="text-neutral-200">Google News</span> and{" "}
              <span className="text-neutral-200">GDELT</span> — plus AI analysis grounded in those signals.
              Reddit and competitor tracking are rolling out next.
            </p>
          </div>
        </div>
        <Link
          to="/"
          data-testid="report-banner-cta"
          className="inline-flex items-center gap-2 bg-[#FF5000] hover:bg-[#FF7333] text-white text-[13px] font-medium px-4 py-2.5 transition-all duration-200 whitespace-nowrap"
        >
          Join the Community <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Summary header */}
      <div className="border border-neutral-800 bg-[#080808] p-5 md:p-6 mb-px flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
            {report.industry} · {report.location}
          </p>
          <p data-testid="report-summary" className="text-white text-[15px] md:text-[17px] leading-relaxed max-w-3xl">
            {report.summary || "Live signals assembled below."}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={handleShare}
            data-testid="report-share-btn"
            className="inline-flex items-center gap-2 border border-[#FF5000] text-[#FF5000] hover:bg-[#FF5000] hover:text-white text-[13px] font-medium px-4 py-2.5 transition-all duration-200"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button
            onClick={() => exportXlsx(report)}
            data-testid="report-export-btn"
            className="inline-flex items-center gap-2 border border-[#FF5000] text-[#FF5000] hover:bg-[#FF5000] hover:text-white text-[13px] font-medium px-4 py-2.5 transition-all duration-200"
          >
            <Download className="w-4 h-4" /> Export XLSX
          </button>
          <button
            onClick={onReset}
            data-testid="report-reset-btn"
            className="inline-flex items-center gap-2 border border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white text-[13px] px-4 py-2.5 transition-all duration-200"
          >
            <RotateCcw className="w-4 h-4" /> New sweep
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-neutral-900 border border-neutral-900 ds-scanlines">
        {/* Search trends */}
        <Card testid="card-search-trends" icon={TrendingUp} title="Search trends · Google" span="md:col-span-12"
          meta={t.trends_api_ok ? `Interest ${t.direction}` : "Live autocomplete"}>
          {t.available ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#FF5000] mb-3">
                  {(t.rising || []).length ? `Rising searches · ${t.timeframe || "past 12 mo"}` : "What people search for"}
                </p>
                <ul className="divide-y divide-neutral-900">
                  {((t.rising || []).length ? t.rising : (t.suggestions || []).slice(0, 7).map((q) => ({ query: q }))).map((r) => (
                    <li key={r.query} className="flex items-center justify-between py-2.5 gap-4">
                      <span className="text-white text-[14px] truncate">{r.query}</span>
                      {r.value != null && (
                        <span className="ds-mono text-[#FF5000] text-[12px] flex-shrink-0" title={`Growth over the ${t.timeframe || "past 12 months"}`}>
                          {typeof r.value === "number" ? `+${r.value.toLocaleString()}%` : r.value}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-3">
                  {(t.rising || []).length ? "Popular searches" : "Related queries"}
                </p>
                <ul className="divide-y divide-neutral-900">
                  {((t.rising || []).length ? (t.suggestions || []) : (t.suggestions || []).slice(7)).slice(0, 8).map((q) => (
                    <li key={q} className="flex items-center justify-between py-2.5 gap-4">
                      <span className="text-neutral-200 text-[14px] truncate">{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <p className="text-neutral-500 text-[13px]">Search-trend data didn't return for this query right now — the rest of the report below is live. Try a broader term or run the sweep again shortly.</p>
          )}
        </Card>

        {/* News */}
        <Card testid="card-news" icon={Newspaper} title="In the news" span="md:col-span-6" meta={`${news.length} sources`}>
          {news.length ? (
            <div className="divide-y divide-neutral-900">
              {news.map((a) => <ArticleRow key={a.url || a.title} a={a} />)}
            </div>
          ) : (
            <p className="text-neutral-500 text-[13px]">No recent news matched this query.</p>
          )}
        </Card>

        {/* GDELT + broad industry coverage */}
        <Card testid="card-coverage" icon={Globe} title="Industry & media signal" span="md:col-span-6" meta={`${coverage.length} articles`}>
          {coverage.length ? (
            <div className="divide-y divide-neutral-900">
              {coverage.map((a) => <ArticleRow key={a.url || a.title} a={a} />)}
            </div>
          ) : (
            <p className="text-neutral-500 text-[13px]">No additional industry coverage matched this query.</p>
          )}
        </Card>

        {/* Investment / market movement (hidden when no ticker match) */}
        {market && (
          <Card
            testid="card-market"
            icon={LineChart}
            title="Market movement · Investment signals"
            span="md:col-span-12"
            meta={market.as_of ? `Close ${market.as_of} · Yahoo Finance` : "Yahoo Finance"}
          >
            <ul data-testid="market-signal-list" className="divide-y divide-neutral-900">
              {market.signals.map((s) => <MarketRow key={s.symbol} s={s} />)}
            </ul>
            <p className="text-[11px] text-neutral-600 mt-4 leading-relaxed">
              Listed companies and funds tied to this industry — plus the S&amp;P 500 for context. Not financial advice.
            </p>
          </Card>
        )}

        {/* Opportunities (AI) */}
        <Card testid="card-opportunities" icon={Sparkles} title="Opportunities detected" span="md:col-span-12" meta="AI analysis of live signals">
          {opps.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
              {opps.map((o) => (
                <div key={o.title} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 bg-[#FF5000] flex-shrink-0" />
                  <div>
                    <p className="text-white text-[15px] tracking-tight">{o.title}</p>
                    <p className="text-neutral-400 text-[13px] leading-snug mt-1">{o.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-neutral-500 text-[13px]">Not enough live signal to derive opportunities for this query.</p>
          )}
        </Card>

        {/* Coming soon */}
        <Card testid="card-coming-soon" icon={Lock} title="Coming soon" span="md:col-span-12" meta="In development">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-900">
            {(report.coming_soon || []).map((c) => (
              <div key={c.name} className="bg-[#070707] p-5 opacity-60">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-3.5 h-3.5 text-neutral-500" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border border-neutral-700 px-1.5 py-0.5">Soon</span>
                </div>
                <p className="text-neutral-200 text-[14px] font-medium">{c.name}</p>
                <p className="text-neutral-500 text-[12px] leading-snug mt-1">{c.desc}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-600 mt-6 ds-mono">
        Live data · Google Trends · Google News · GDELT · AI analysis · more sources rolling out
      </p>

      {/* Donation prompt */}
      <div
        data-testid="report-donate"
        className="mt-6 border border-neutral-800 bg-[#080808] p-6 md:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div className="flex items-start gap-3">
          <Heart className="w-5 h-5 text-[#FF5000] flex-shrink-0 mt-0.5" />
          <p className="text-neutral-300 text-[14px] leading-relaxed max-w-2xl">
            If you find this tool helpful, consider donating to us — it will help us improve the
            platform and develop it even further. Thank you!
          </p>
        </div>
        <a
          href="https://ko-fi.com/datasniper"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="report-donate-btn"
          className="inline-flex items-center gap-2 bg-[#FF5000] hover:bg-[#FF7333] text-white text-[13px] font-medium px-5 py-2.5 transition-all duration-200 whitespace-nowrap"
        >
          <Heart className="w-4 h-4" /> Donate
        </a>
      </div>
    </div>
  );
}
